/* GifReactionPicker — universal picker for inserting an animated GIF
 * into a chat/comment/DM/Tie-board context. Wire it next to any text
 * input by mounting <GifReactionPickerButton onPick={(url)=>...}/>
 *
 * `onPick(url, gif)` fires with the chosen Giphy CDN URL plus the full
 * catalog entry. The caller decides what to do — insert markdown,
 * attach to a message payload, post as standalone reaction, etc.
 */
import React, { useEffect, useMemo, useState } from "react";
import { X, Search, Smile, Sparkles, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import SafeImg from "@/components/SafeImg";

/* Module-local cache so opening the picker on different surfaces in the
 * same session doesn't re-fetch the catalog. */
let _CATALOG_CACHE = null;

export function GifReactionPickerButton({ onPick, className = "", testIdPrefix = "gif" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`${testIdPrefix}-picker-trigger`}
        className={`inline-flex items-center gap-1 text-xs text-white/60 hover:text-white px-2 py-1  hover:bg-white/5 ${className}`}
        aria-label="Insert reaction GIF"
        title="Insert reaction GIF"
      >
        <Smile size={14} /> GIF
      </button>
      <GifReactionPickerModal
        open={open}
        onClose={() => setOpen(false)}
        onPick={(url, gif) => { onPick && onPick(url, gif); setOpen(false); }}
        testIdPrefix={testIdPrefix}
      />
    </>
  );
}

export function GifReactionPickerModal({ open, onClose, onPick, testIdPrefix = "gif" }) {
  const [catalog, setCatalog] = useState(_CATALOG_CACHE);
  const [myMemes, setMyMemes] = useState([]);
  const [query, setQuery] = useState("");
  const [pasteUrl, setPasteUrl] = useState("");
  // Live Giphy state — populated by debounced search OR by /trending when
  // the query is empty. We keep the cache in component-state so re-opening
  // the picker with the same query is instant.
  const [giphyGifs, setGiphyGifs] = useState([]);
  const [giphyLoading, setGiphyLoading] = useState(false);
  const [giphyError, setGiphyError] = useState(false);
  const [giphyOffset, setGiphyOffset] = useState(0);
  const [giphyTotal, setGiphyTotal] = useState(0);

  useEffect(() => {
    if (!open) return;
    if (!_CATALOG_CACHE) {
      api.get("/reaction-gifs")
        .then((r) => { _CATALOG_CACHE = r.data; setCatalog(r.data); })
        .catch(() => setCatalog({ gifs: [], categories: [] }));
    }
    // Fetch the user's personal meme library so it appears under "My Memes".
    // Silently no-ops for signed-out users (401).
    api.get("/me/memes")
      .then((r) => setMyMemes(r.data?.memes || []))
      .catch(() => setMyMemes([]));
  }, [open]);

  // Debounced Giphy fetch — runs whenever the query changes (400ms idle).
  // Empty query → trending. Non-empty → live search. Resets pagination on
  // every new query so we don't mix offsets between different searches.
  useEffect(() => {
    if (!open) return;
    setGiphyOffset(0);
    setGiphyError(false);
    const q = query.trim();
    const handle = setTimeout(async () => {
      setGiphyLoading(true);
      try {
        const endpoint = q ? `/giphy/search?q=${encodeURIComponent(q)}&limit=50` : `/giphy/trending?limit=50`;
        const r = await api.get(endpoint);
        setGiphyGifs(r.data?.gifs || []);
        setGiphyTotal(r.data?.pagination?.total_count || 0);
      } catch {
        setGiphyGifs([]);
        setGiphyError(true);
      } finally {
        setGiphyLoading(false);
      }
    }, q ? 400 : 0);
    return () => clearTimeout(handle);
  }, [open, query]);

  const loadMoreGiphy = async () => {
    const nextOffset = giphyOffset + 50;
    setGiphyLoading(true);
    try {
      const q = query.trim();
      const endpoint = q
        ? `/giphy/search?q=${encodeURIComponent(q)}&limit=50&offset=${nextOffset}`
        : `/giphy/trending?limit=50&offset=${nextOffset}`;
      const r = await api.get(endpoint);
      setGiphyGifs((prev) => [...prev, ...(r.data?.gifs || [])]);
      setGiphyOffset(nextOffset);
    } catch {
      setGiphyError(true);
    } finally {
      setGiphyLoading(false);
    }
  };

  // Memes the user saved in Meme Studio — rendered as their OWN section in
  // the picker so they're easy to find. Empty array when user has no saves
  // (or is signed out).
  const myMemeEntries = useMemo(() => myMemes.map((m) => ({
    id: `meme_${m.id}`,
    url: m.url,
    category: "my_memes",
    keywords: `${m.title || ""} meme custom`,
    _meme_id: m.id,
  })), [myMemes]);

  // Local Vault library — curated reaction GIFs + funny avatars from the
  // backend. Always available even if Giphy or memes endpoints fail.
  const vaultEntries = useMemo(() => catalog?.gifs || [], [catalog]);

  // Filter both local sections by the search query (substring match on
  // keywords / id) so typing narrows the local picks alongside Giphy.
  const matches = (g, q) => {
    if (!q) return true;
    return (g.keywords || "").toLowerCase().includes(q)
        || g.id.toLowerCase().includes(q);
  };
  const q = query.trim().toLowerCase();
  const filteredMyMemes = useMemo(() => myMemeEntries.filter((g) => matches(g, q)), [myMemeEntries, q]);
  const filteredVault   = useMemo(() => vaultEntries.filter((g) => matches(g, q)), [vaultEntries, q]);

  // Accept any direct image/gif URL the user pastes. We're permissive:
  // anything ending in .gif/.webp/.png or hosted on giphy/tenor/imgur is fine.
  const isPasteValid = (() => {
    const u = pasteUrl.trim();
    if (!u) return false;
    if (!/^https?:\/\//i.test(u)) return false;
    return /\.(gif|webp|png|jpg|jpeg)(\?|$)/i.test(u)
        || /giphy\.com|tenor\.com|imgur\.com|tenor\.googleapis\.com/i.test(u);
  })();
  const submitPaste = () => {
    if (!isPasteValid) return;
    onPick && onPick(pasteUrl.trim(), { id: "custom", category: "custom", keywords: "custom" });
    setPasteUrl("");
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[90] bg-black/80 backdrop-blur-sm flex items-start sm:items-center justify-center p-2 sm:p-4" data-testid={`${testIdPrefix}-picker`}>
      <div className="w-full max-w-3xl bg-[#0a0a0a] border border-white/15">
        <div className="border-b border-white/10 p-3 flex items-center gap-2">
          <Search size={14} className="text-white/50 flex-shrink-0" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="search reaction GIFs…"
            autoFocus
            data-testid={`${testIdPrefix}-search`}
            className="flex-1 bg-transparent border-0 text-white outline-none text-sm placeholder-white/30"
          />
          <button onClick={onClose} className="text-white/55 hover:text-white p-1" data-testid={`${testIdPrefix}-close`}>
            <X size={16} />
          </button>
        </div>
        <div className="p-2 border-b border-white/10 flex flex-wrap gap-2 items-center justify-end">
          <Link
            to="/meme-studio"
            onClick={() => onClose && onClose()}
            data-testid={`${testIdPrefix}-make-meme`}
            className="inline-flex items-center gap-1 px-2 py-1  bg-gradient-to-r from-[#a78bfa] to-[#ec4899] text-black text-[10px] uppercase tracking-widest font-mono font-bold hover:opacity-90"
            title="Make your own meme in the Meme Studio"
          >
            <Sparkles size={11} /> Make a Meme
          </Link>
        </div>
        {/* Paste any GIF URL — lets users drop in their own from Giphy/Tenor/
            Imgur, etc. without waiting for the curated catalog to grow. */}
        <div className="p-2 border-b border-white/10 flex flex-wrap gap-2 items-center">
          <span className="font-mono text-[9px] uppercase tracking-widest text-white/45 shrink-0">// Paste any GIF URL</span>
          <input
            type="url"
            value={pasteUrl}
            onChange={(e) => setPasteUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submitPaste(); }}
            placeholder="https://media.giphy.com/.../giphy.gif  or  https://media.tenor.com/.../tenor.gif"
            data-testid={`${testIdPrefix}-paste-url`}
            className="flex-1 min-w-[200px] bg-black/40 border border-white/15 px-2 py-1 text-[11px] font-mono text-white outline-none focus:border-[#a78bfa] "
          />
          <button
            type="button"
            onClick={submitPaste}
            disabled={!isPasteValid}
            data-testid={`${testIdPrefix}-paste-submit`}
            className="px-2 py-1 bg-[#a78bfa] text-black font-mono text-[10px] uppercase tracking-widest  hover:bg-[#c4b1ff] disabled:opacity-30 disabled:cursor-not-allowed"
          >
            Insert
          </button>
        </div>
        <div className="p-3 max-h-[60vh] overflow-y-auto space-y-4">
          {/* Live Giphy search — hero section so people see fresh, guaranteed
              working content first. Renders trending when query is empty so
              opening the picker is never an empty screen. */}
          <div>
            <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#22d3ee] mb-2 flex items-center justify-between gap-2">
              <span>// {query.trim() ? `Giphy results · "${query.trim()}"` : "Giphy trending"} {giphyGifs.length ? `· ${giphyGifs.length}` : ""}</span>
              <span className="text-white/40 text-[9px]">via giphy.com</span>
            </div>
            {giphyLoading && giphyGifs.length === 0 ? (
              <div className="text-white/40 text-xs font-mono text-center py-8">Loading from Giphy…</div>
            ) : giphyError && giphyGifs.length === 0 ? (
              <div className="text-white/45 text-xs font-mono text-center py-6">Couldn&apos;t reach Giphy — your Vault library below still works.</div>
            ) : giphyGifs.length === 0 ? (
              <div className="text-white/40 text-xs font-mono text-center py-6">No Giphy results for &ldquo;{query.trim()}&rdquo;.</div>
            ) : (
              <>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                  {giphyGifs.map((g) => (
                    <button
                      key={`giphy_${g.id}`}
                      type="button"
                      onClick={() => onPick && onPick(g.url, { id: `giphy_${g.id}`, category: "giphy", keywords: g.title })}
                      data-testid={`${testIdPrefix}-pick-giphy-${g.id}`}
                      className="aspect-square bg-black/40 border border-white/10  overflow-hidden hover:border-[#22d3ee]/60 hover:scale-[1.02] transition"
                      title={g.title}
                    >
                      <SafeImg
                        src={g.preview_url || g.url}
                        fallbacks={g.url ? [g.url] : []}
                        alt={g.title}
                        className="w-full h-full object-cover"
                        placeholderLabel="▒▒"
                      />
                    </button>
                  ))}
                </div>
                {/* Pagination — only show Load More if there's clearly more. */}
                {(giphyOffset + giphyGifs.length) < giphyTotal ? (
                  <div className="flex justify-center mt-3">
                    <button
                      type="button"
                      onClick={loadMoreGiphy}
                      disabled={giphyLoading}
                      data-testid={`${testIdPrefix}-giphy-load-more`}
                      className="px-3 py-1.5 border border-[#22d3ee]/40 text-[#22d3ee] font-mono text-[10px] uppercase tracking-widest  hover:bg-[#22d3ee]/10 disabled:opacity-40"
                    >
                      {giphyLoading ? "Loading…" : "Load more from Giphy"}
                    </button>
                  </div>
                ) : null}
              </>
            )}
          </div>

          {/* My Memes — user's own Meme Studio saves. Pink-accent section so
              it's visually distinct from the curated Vault library. Only
              renders when the user has saves AND any survive the search filter. */}
          {filteredMyMemes.length > 0 ? (
            <div>
              <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#ec4899] mb-2 flex items-center gap-2">
                <span>// My Memes · {filteredMyMemes.length}</span>
                <span className="text-white/40 text-[9px]">made in Meme Studio</span>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                {filteredMyMemes.map((g) => (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => {
                      onPick && onPick(g.url, g);
                      // Track usage so the creator can get virality coins.
                      if (g._meme_id) {
                        api.post(`/memes/${g._meme_id}/use`).catch(() => {});
                      }
                    }}
                    data-testid={`${testIdPrefix}-pick-${g.id}`}
                    className="aspect-square bg-black/40 border border-[#ec4899]/30  overflow-hidden hover:border-[#ec4899]/80 hover:scale-[1.02] transition"
                    title={g.keywords}
                  >
                    <SafeImg
                      src={g.url}
                      fallbacks={g.fallback_url ? [g.fallback_url] : []}
                      alt={g.id}
                      className="w-full h-full object-cover"
                      placeholderLabel="▒▒"
                    />
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {/* Vault library — curated reaction GIFs + funny avatar artwork.
              Always present (never lost when Giphy is empty). */}
          {filteredVault.length > 0 ? (
            <div>
              <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/45 mb-2 flex items-center gap-2">
                <span>// Vault library · {filteredVault.length}</span>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                {filteredVault.map((g) => (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => onPick && onPick(g.url, g)}
                    data-testid={`${testIdPrefix}-pick-${g.id}`}
                    className="aspect-square bg-black/40 border border-white/10  overflow-hidden hover:border-white/40 hover:scale-[1.02] transition"
                    title={g.keywords}
                  >
                    <SafeImg
                      src={g.url}
                      fallbacks={g.fallback_url ? [g.fallback_url] : []}
                      alt={g.id}
                      className="w-full h-full object-cover"
                      placeholderLabel="▒▒"
                    />
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* Lightweight inline GIF renderer — for messages/comments where the
 * stored content is a `[gif:url]` token. Uses SafeImg so a 404 from a
 * deleted meme or expired Giphy URL silently degrades to a redacted
 * block instead of a permanent broken-image icon. */
export function InlineGif({ url, className = "" }) {
  if (!url) return null;
  return (
    <SafeImg
      src={url}
      alt=""
      className={`rounded max-w-full max-h-[260px] block ${className}`}
      placeholderLabel="GIF UNAVAILABLE"
      data-testid="inline-gif"
    />
  );
}

/* Renders message text inline, transforming any `[gif:url]` tokens into
 * <InlineGif> images. Used in chat surfaces (DMs, comments, threads). */
export function MessageWithGifs({ content, className = "" }) {
  if (!content) return null;
  const re = /\[gif:(https?:\/\/[^\]\s]+)\]/g;
  const parts = [];
  let last = 0; let i = 0; let m;
  while ((m = re.exec(content)) !== null) {
    if (m.index > last) {
      parts.push({ k: `t${i++}`, type: "text", value: content.slice(last, m.index) });
    }
    parts.push({ k: `g${i++}`, type: "gif", value: m[1] });
    last = m.index + m[0].length;
  }
  if (last < content.length) {
    parts.push({ k: `t${i++}`, type: "text", value: content.slice(last) });
  }
  return (
    <div className={`leading-relaxed whitespace-pre-wrap break-words text-sm space-y-2 ${className}`}>
      {parts.map((p) =>
        p.type === "gif"
          ? <InlineGif key={p.k} url={p.value} />
          : <span key={p.k}>{p.value}</span>
      )}
    </div>
  );
}

